import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { FLAG_OPTIONS } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { PermissionContext } from "@contexts/PermissionContext";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, uppercase, formatDateForRequest, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

export default function AnnouncementList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasEditPermission, hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchFilters, setSearchFilters] = useState([]);
  const [announcementList, setAnnouncementList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.ANNOUNCEMENT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isAnnouncementListLoading, setIsAnnouncementListLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  const { creatorList, postClassList, departmentList, fetchSearchInfo } = useSearchInfo();

  useEffect(() => {
    fetchData();
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  // const columns = ["bulletinSeq", "title", "viewCnt", "createrDeptCdName", "createrName", "createdAt"];
  const columns = ["bulletinSeq", "title", "viewCnt", "createrName", "createdAt"];

  const columnConfig = {
    bulletinSeq: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.bulletin-seq"),
    },
    title: {
      style: {
        width: "60%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.title"),
    },
    viewCnt: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.view-count"),
    },
    // createrDeptCdName: {
    //   style: {
    //     width: "15%",
    //     whiteSpace: "normal",
    //     textAlign: "left",
    //     overflowWrap: "initial",
    //   },
    //   headerRender: () => t("bulletin.creator-department"),
    //   customRender: (value) => translateDepartment(t, value),
    // },
    createrName: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: {
        width: "12%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.creation-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  const searchFields = [
    {
      name: "title",
      label: t("bulletin.title"),
      placeholder: t("bulletin.title-placeholder"),
    },
    {
      name: "mainPopupFlag",
      label: t("bulletin.main-popup"),
      type: "select",
      placeholder: t("bulletin.main-popup-placeholder"),
      options: [...FLAG_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))],
    },
    {
      name: "createrId",
      label: t("bulletin.creator"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("bulletin.creator-placeholder"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    // {
    //   name: "createrDeptCd",
    //   label: t("bulletin.creator-department"),
    //   type: "select",
    //   keepRawLabel: true,
    //   placeholder: t("bulletin.creator-department-placeholder"),
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    // },
    {
      name: "postClassCd",
      label: t("bulletin.post-class"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("bulletin.post-class-options-placeholder"),
      options: [...postClassList],
    },
    {
      name: "postStartDtFrom",
      label: t("bulletin.post-start-date-from"),
      type: "date",
      placeholder: t("bulletin.post-start-date-from"),
    },
    {
      name: "postStartDtTo",
      label: t("bulletin.post-start-date-to"),
      placeholder: t("bulletin.post-start-date-to"),
      type: "date",
    },
    {
      name: "createdAtFrom",
      label: t("common.create-at-from"),
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("common.create-at-to"),
      placeholder: t("common.create-at-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const handleDeleteAnnouncement = () => {
    setShowDeleteModal(true);
  };

  const deleteData = useMutationData(
    queryKeys.deleteAnnouncement,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.BULLETINS, selectedRows);
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        showSuccessToast(t("bulletin.delete-announcement-successfully", { count: selectedRows.length }));
        setSelectedRows([]);
        queryClient.invalidateQueries(queryKeys.getAnnouncements);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        showErrorToast(t("bulletin.delete-announcement-failed"));
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const bodyRequest = selectedRows;
      await deleteData.mutateAsync(bodyRequest);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting announcements:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddAnnouncement = () => {
    navigate("/announcements/new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.bulletinSeq}`);
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDate function
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const getListData = async (bodyRequest = {}) => {
    setIsAnnouncementListLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      const response = await getIndexDBData(STORES.BULLETINS, {
        pageSize,
        startRow,
        filters,
        sortKey: "bulletinSeq",
      });

      if (response && typeof response !== "string") {
        setAnnouncementList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching announcements from IndexedDB:", error);
      showErrorToast(t("bulletin.get-announcement-list-error"));
    } finally {
      setIsAnnouncementListLoading(false);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="announcement-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="announcement-list-header">
        <div className="flex items-center justify-between">
          <h1
            className={`text-[28px] font-bold ${colors.textPrimary} uppercase`}
            data-testid="announcement-list-title"
          >
            {uppercase(t("bulletin.announcement-list"))}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="announcement-list-controls">
        <TableHeaderControls
          controlsTestId="announcement-list-table-header-controls"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDeleteAnnouncement}
          onAdd={handleAddAnnouncement}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("bulletin.add-announcement")}
          titleDeleteButton={t("bulletin.delete-announcement")}
          isDisplayAddButton={hasEditPermission}
          isDisplayDeleteButton={hasDeletePermission}
          searchInputTestId="announcement-list-search-input"
          addButtonTestId="announcement-list-add-button"
          deleteButtonTestId="announcement-list-delete-button"
          advancedSearchButtonTestId="announcement-list-advanced-search-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="announcement-list-advanced-search">
          <AdvancedSearch
            data-testid="announcement-list-advanced-search-panel"
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Announcement Table */}
      <div className="flex-grow min-h-0" data-testid="announcement-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={announcementList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isAnnouncementListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="bulletinSeq"
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="announcement-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("bulletin.delete-announcement"))}
        description={t("bulletin.delete-announcement-warning", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  );
}
